import { useEffect, useState } from "react";

import { ButtonInput } from "../shared/Inputs/ButtonInput";
import NotesOutput from "../shared/Notes/NotesOutput";
import usePartRequests from "../../data/usePartRequests";
import { ResourceType } from "../../models/Note";
import { IPartRequest, PartRequestSource } from "../../models/partRequests/IPartRequest";
import { toPartRequestYardDetails } from "../../utils/partRequests";

const inputClasses =
  "block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500";

/**
 * The yard's own notes on a request: part number, paint code and a running log. Editable on
 * PartPro requests too - the buyer's text stays as it came in, these sit alongside it.
 */
export default function PartRequestYardNotes({ request }: { request: IPartRequest }) {
  const { updateYardDetails } = usePartRequests(undefined, "PartRequestYardNotes");
  const [editing, setEditing] = useState(false);
  const [partNumber, setPartNumber] = useState(request.partNumber ?? "");
  const [paintCode, setPaintCode] = useState(request.paintCode ?? "");

  useEffect(() => {
    if (editing) return;
    setPartNumber(request.partNumber ?? "");
    setPaintCode(request.paintCode ?? "");
  }, [request.partNumber, request.paintCode, editing]);

  const save = () =>
    updateYardDetails.mutate(
      { id: request.id, details: toPartRequestYardDetails({ partNumber, paintCode }) },
      { onSuccess: () => setEditing(false) }
    );

  return (
    <div className="space-y-4 p-4">
      {editing ? (
        <div className="grid grid-cols-1 items-end gap-3 sm:grid-cols-[1fr_1fr_auto]">
          <div>
            <label htmlFor="part-request-part-number" className="block text-xs font-medium uppercase tracking-wide text-gray-500">Part number</label>
            <input id="part-request-part-number" className={`mt-1 ${inputClasses}`} value={partNumber} placeholder="OE number, e.g. 8X0807065" onChange={(e) => setPartNumber(e.target.value)} />
          </div>
          <div>
            <label htmlFor="part-request-paint-code" className="block text-xs font-medium uppercase tracking-wide text-gray-500">Paint code</label>
            <input id="part-request-paint-code" className={`mt-1 uppercase ${inputClasses}`} value={paintCode} placeholder="e.g. LY7W, Z3M" onChange={(e) => setPaintCode(e.target.value)} />
          </div>
          <div className="flex gap-2">
            <ButtonInput label="Cancel" classes="!bg-white border border-gray-300 !text-gray-700 hover:!bg-gray-50" onClick={() => setEditing(false)} />
            <ButtonInput label="Save" classes="" isLoading={updateYardDetails.isLoading} onClick={save} />
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-end justify-between gap-3">
          <dl className="grid grid-cols-2 gap-6">
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Part number</dt>
              <dd className="mt-1 font-mono text-sm text-gray-900">{request.partNumber ?? "—"}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Paint code</dt>
              <dd className="mt-1 font-mono text-sm text-gray-900">{request.paintCode ?? "—"}</dd>
            </div>
          </dl>
          <button type="button" onClick={() => setEditing(true)} className="text-sm text-primary-600 hover:underline">
            {request.partNumber || request.paintCode ? "Edit" : "Add part number / paint code"}
          </button>
        </div>
      )}
      <p className="text-xs text-gray-500">
        {request.source === PartRequestSource.PartPro
          ? "Kept in Wilma - the buyer doesn't see these or the notes below."
          : request.isOnNetwork
            ? "Part number and paint code are sent to the PartPro network with the request. The notes below stay in Wilma."
            : "Part number and paint code go with the request if it's sent to the PartPro network. The notes below stay in Wilma."}
      </p>

      <div className="-mx-4 border-t border-gray-100 pt-2">
        <NotesOutput resourceId={request.id} resourceType={ResourceType.PartRequest} shouldScroll={false} />
      </div>
    </div>
  );
}
